import { useMutation } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { toast } from 'sonner';
import { orpc } from '@/api/rpc/client';
import {
  useInvalidateSpaRecords,
  useInvalidateSpaRequests,
} from './useSpaRequests';

/**
 * Deletes a persisted SPA record, refreshes the record and request caches it
 * touched, and toasts the outcome. Resolves `true` on success so the caller
 * can close its dialog or navigate away.
 */
export function useDeleteSpaRecord() {
  const gt = useGT();
  const invalidateSpaRecords = useInvalidateSpaRecords();
  const invalidateSpaRequests = useInvalidateSpaRequests();
  const deleteSpa = useMutation(orpc.pricing.spas.delete.mutationOptions());

  async function deleteSpaRecord(id: string): Promise<boolean> {
    try {
      const result = await deleteSpa.mutateAsync({ id });
      invalidateSpaRecords(id);
      invalidateSpaRequests(result.spaRequestId ?? undefined);
      toast.success(
        gt('SPA record deleted', {
          context: 'Toast shown after deleting a persisted SPA record',
        }),
      );
      return true;
    } catch {
      toast.error(
        gt('Could not delete SPA record', {
          context: 'Toast shown when deleting a persisted SPA record fails',
        }),
      );
      return false;
    }
  }

  return { deleteSpaRecord, isPending: deleteSpa.isPending };
}
